import { JSX } from "react";
import { Fade } from "@/shared/ui";
import { useAssessmentFlow, useAssessmentStore } from "@/service/assessment";
import { useUserStore } from "@/service/user";
import { useGetAvailableReviewPeriods } from "@/service/review-period";
import { Header, InitialStep, StepConstructor, PerformanceReviewStep, FinalCard, TeamResultsPage } from "./_components";

export const AssessmentPage = (): JSX.Element => {
  const {
    submitStepData,
    onLoadPrevStep,
    isLoadingNextStep,
    isLoadingPrevStep,
    isSendingStepData,
    isLoadingInitialStepData,
    showInitialStep,
    setShowInitialStep,
    collaboratorList,
    colleagueList,
    resetToInitial,
    showFinal,
  } = useAssessmentFlow();
  const { user } = useUserStore();
  const { stepData, assessmentId } = useAssessmentStore();
  const { data: periods = [] } = useGetAvailableReviewPeriods();

  const viewKey = (() => {
    if (showFinal) return "final";

    if (showInitialStep) {
      return !isLoadingInitialStepData ? "initial" : "welcome";
    }

    if (assessmentId && stepData) {
      return `step:${stepData.stepId}`;
    }

    return "welcome";
  })();

  const renderContent = (): JSX.Element => {
    if (user?.teamlead && periods[0]?.is_open === false) {
      return <TeamResultsPage />;
    }

    if (showFinal) {
      return <FinalCard onClick={resetToInitial} />;
    }

    if (showInitialStep && collaboratorList && colleagueList && !isLoadingInitialStepData) {
      return (
        <InitialStep
          isLoadingNextStep={isLoadingNextStep}
          collaboratorList={collaboratorList}
          colleagueList={colleagueList}
        />
      );
    }

    if (assessmentId && stepData) {
      return (
        <StepConstructor
          {...stepData}
          onSubmitData={submitStepData}
          onClickBack={onLoadPrevStep}
          isLoadingSubmit={isSendingStepData || isLoadingNextStep}
          isLoadingBack={isLoadingPrevStep}
          assessmentId={assessmentId}
        />
      );
    }

    return (
      <PerformanceReviewStep
        isLoadingData={isLoadingInitialStepData}
        onClickStart={() => setShowInitialStep(true)}
        isTeamLead={user?.teamlead}
      />
    );
  };

  return (
    <div data-testid="assessment-page" className="bg-brand flex min-h-screen w-full flex-col">
      <Header />
      <Fade
        data-testid="assessment-content"
        className="container-wrapper box-border flex w-full justify-center !p-[30px] !pb-[120px]"
        stateKey={viewKey}
      >
        {renderContent()}
      </Fade>
    </div>
  );
};
